import { useT } from '@planetra/i18n';
import { Button, Callout } from '@planetra/ui';
import { useApiError, type ApiFailure } from '../api';

export interface ApiErrorStateProps {
  readonly failure: ApiFailure;
  /**
   * Offered only when the failure is one retrying can plausibly fix — the
   * component decides that from the failure, not the caller, so a retry button
   * never appears on a 403 where pressing it repeats the same answer.
   */
  readonly onRetry?: (() => void) | undefined;
  readonly className?: string | undefined;
}

/**
 * A failed request, rendered in place (P0-WEB-04).
 *
 * The default building block for the error half of planning.md §6's
 * loading/empty/error requirement. In place rather than global: PRD §15 wants
 * the failure shown where the thing that failed would have been, so the rest of
 * the page stays usable and the user can see what still worked.
 *
 * Routes with a specific story to tell — an imagery search with no results, an
 * AOI over the plan limit — write their own state instead of reaching for this.
 * This is the honest default, not the answer to everything.
 */
export function ApiErrorState({ failure, onRetry, className }: ApiErrorStateProps) {
  const { t } = useT();
  const describe = useApiError();
  const { title, description, retryable } = describe(failure);

  return (
    <Callout
      tone={failure.kind === 'offline' ? 'warning' : 'danger'}
      title={title}
      className={className}
      action={
        retryable && onRetry !== undefined ? (
          <Button size="sm" onClick={onRetry}>
            {t('common:actions.retry')}
          </Button>
        ) : undefined
      }
    >
      {description}
    </Callout>
  );
}
